import { type UniversalIssue } from '@deque/axe-devtools-issues-utils';
import { apiRequest } from './apiRequest';
import type {
  Enterprise,
  IntegrationConnection,
  IntegrationTemplate,
  IssueMetadata,
  ProjectsAndIssueTypes
} from '../../types';

type BaseRequestParams = {
  apiHostname: string;
  token: string;
};

/** Get an enterprise by its `id`. */
type GetEnterpriseByIdParams = BaseRequestParams & {
  enterpriseId: string;
};
export const getEnterpriseById = ({
  apiHostname,
  token,
  enterpriseId
}: GetEnterpriseByIdParams): Promise<Enterprise> => {
  return apiRequest({
    apiHostname,
    endpoint: `/api/billing/v2/enterprises/${enterpriseId}`,
    token
  });
};

/** Get an enterprise member by its `id`. */
type GetEnterpriseMembersParams = BaseRequestParams & {
  enterpriseId: string;
};
type EnterpriseMember = {
  user_id: string;
  product_access: {
    product_slug: string;
    access_type: string;
  }[];
};
export const getEnterpriseMemberById = ({
  apiHostname,
  token,
  enterpriseId
}: GetEnterpriseMembersParams): Promise<EnterpriseMember[]> => {
  return apiRequest({
    apiHostname,
    endpoint: `/api/billing/v2/enterprises/${enterpriseId}/members`,
    token
  });
};

/** Get all integration connections for an enterprise. */
type GetIntegrationConnectionsParams = BaseRequestParams & {
  enterpriseId: string;
};
export const getIntegrationConnections = ({
  apiHostname,
  token,
  enterpriseId
}: GetIntegrationConnectionsParams): Promise<IntegrationConnection[]> => {
  return apiRequest({
    apiHostname,
    endpoint: `/api/integrations/enterprises/${enterpriseId}/connections`,
    token
  });
};

/** Get all templates for a connection. */
type GetTemplatesParams = BaseRequestParams & {
  enterpriseId: string;
  connectionId?: string;
};
export const getTemplates = ({
  apiHostname,
  token,
  enterpriseId,
  connectionId
}: GetTemplatesParams): Promise<IntegrationTemplate[]> => {
  const endpoint = connectionId
    ? `/api/integrations/enterprises/${enterpriseId}/connections/${connectionId}/templates`
    : `/api/integrations/enterprises/${enterpriseId}/templates`;

  return apiRequest({
    apiHostname,
    endpoint,
    token
  });
};

/** Get a sent issue by its `id`. */
type GetSentIssueByIdParams = BaseRequestParams & {
  enterpriseId: string;
  issueId: string;
};
export const getSentIssueById = ({
  apiHostname,
  token,
  enterpriseId,
  issueId
}: GetSentIssueByIdParams): Promise<IssueMetadata[]> => {
  return apiRequest({
    apiHostname,
    endpoint: `/api/enterprises/${enterpriseId}/issue-metadata?issue_id=${issueId}` /*`/api/tests/${testId}/issue-metadata`*/,
    token
  });
};

/** Get sent issues by their `ids`. */
type GetSentIssuesByIdsParams = BaseRequestParams & {
  enterpriseId: string;
  issueIds: string[];
};
export const getSentIssuesByIds = ({
  apiHostname,
  token,
  enterpriseId,
  issueIds
}: GetSentIssuesByIdsParams): Promise<IssueMetadata[]> => {
  const issueIdsString = issueIds.join(',');
  const issueIdsAmount = issueIds.length;

  return apiRequest({
    apiHostname,
    endpoint: `/api/enterprises/${enterpriseId}/issue-metadata?issue_id=${issueIdsString}&limit=${issueIdsAmount}`,
    token
  });
};

/** Get projects and issue types for a connection. */
type GetProjectsAndIssueTypesParams = BaseRequestParams & {
  enterpriseId: string;
  connectionId: string;
};
export const getProjectsAndIssueTypes = async ({
  apiHostname,
  token,
  enterpriseId,
  connectionId
}: GetProjectsAndIssueTypesParams): Promise<ProjectsAndIssueTypes[]> => {
  const projectsAndIssueTypes = await apiRequest<ProjectsAndIssueTypes[]>({
    apiHostname,
    endpoint: `/api/integrations/enterprises/${enterpriseId}/connections/${connectionId}/projects`,
    token
  });

  return (
    projectsAndIssueTypes
      // Filter out issueTypes that have a property 'subtask' set to true
      .map(project => ({
        ...project,
        issueTypes: project.issueTypes?.filter(issueType => !issueType.subtask)
      }))
      // Filter out projects that don't have any issueTypes left after the previous filter or that don't have the canCreateIssue property set to true
      .filter(
        project =>
          project.issueTypes &&
          project.issueTypes.length > 0 &&
          project.canCreateIssue
      )
  );
};

/** Send an issue to an integration. */
export type SendIssueBody = {
  template_id: string;
  group_id: string;
  product_source_slug: string;
  issues: UniversalIssue[];
  mappedIntegrationFields?: {
    key: string;
    value: unknown;
  }[];
};

type SendIssueParams = BaseRequestParams & {
  enterpriseId: string;
  connectionId: string;
  body: SendIssueBody;
};
export const sendIssue = ({
  apiHostname,
  token,
  enterpriseId,
  connectionId,
  body
}: SendIssueParams): Promise<IssueMetadata[]> => {
  return apiRequest({
    apiHostname,
    endpoint: `/api/integrations/enterprises/${enterpriseId}/connections/${connectionId}/issues`,
    method: 'POST',
    token,
    body
  });
};
